import type { MonetaryAmount } from '@backmarket/http-api/src/api-models/MonetaryAmount'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import type { Camelized } from 'humps'
import { defineStore } from 'pinia'

import type { Discount, DiscountPaymentData } from '../types/discount'

import { formatDiscount } from './discountStore.utils'

interface State {
  basePrice: MonetaryAmount
  discount: Discount
  errorMessage: string
  token: string
  paymentMethod: PaymentMethod | null
}

const EMPTY_DISCOUNT = {
  deductionWithCurrency: null,
  deductedPrice: 0,
  deduction: 0,
  deductionMax: 0,
  id: null,
  referralBase: 0,
}

export const useDiscountStore = defineStore('discountStore', {
  state: (): State => ({
    basePrice: {
      currency: useMarketplace().market.defaultCurrency,
      amount: '0.00',
    },
    discount: { ...EMPTY_DISCOUNT },
    errorMessage: '',
    token: '',
    paymentMethod: null,
  }),
  getters: {
    id: (state): string | null => state.discount.id,
    getDeduction: (state): MonetaryAmount => ({
      currency: state.basePrice.currency,
      amount: String(state.discount.deduction),
    }),
    getDeductedPrice: (state): MonetaryAmount => ({
      currency: state.basePrice.currency,
      amount: String(state.discount.deductedPrice),
    }),
    isApplied(): boolean {
      return !isEmpty(this.id)
    },
    paymentData(): DiscountPaymentData | null {
      if (!this.isApplied) {
        return null
      }

      return {
        id: this.id,
        deduction: this.getDeduction,
        totalDeduction: this.getDeduction,
      }
    },
  },
  actions: {
    reset() {
      this.discount = { ...EMPTY_DISCOUNT }
      this.errorMessage = ''
    },
    setBasePrice(basePrice: MonetaryAmount) {
      if (
        this.basePrice.currency !== basePrice.currency ||
        this.basePrice.amount !== basePrice.amount
      ) {
        this.reset()
        this.basePrice = basePrice
      }
    },
    saveToken(
      token: string,
      responseRaw: Camelized<
        ApiSpecs['/bm/promotion/check']['POST']['response']
      >,
    ) {
      this.reset()
      this.token = token

      const response = formatDiscount(responseRaw)

      this.errorMessage = response.errorMessage

      if (response.isValid) {
        this.discount = response.discount
        this.basePrice = {
          amount: `${response.discount.deductedPrice}`,
          currency: useMarketplace().market.defaultCurrency,
        }
      }
    },
  },
})
